import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { StepType } from "../common";
import {
  AssignmentTypeOption,
  hasTagConditionSchema,
  messageBodyMatchesConditionSchema,
  messageSentimentConditionSchema,
  ruleActionSchema,
  subjectLineConditionSchema,
  topicConditionSchema,
} from "../rules/schemas";
import {
  DateQueryOperator,
  ErrorHandlingStrategy,
  NumericQueryOperator,
  QueryOperator,
  RuleConditionType,
  dateQueryOperators,
  selectionQueryOperators,
  textQueryOperators,
} from "../rules/types";
import {
  AI_STATUSES,
  CONVERSATION_STATUSES_V3,
  MESSAGE_CHANNELS_V3,
  TimeUnit,
} from "./types";

export { TimeUnit } from "./types";

// V3-specific: TICKET_CHANNEL uses MessageChannelV3 strings
export const ticketChannelV3ConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_CHANNEL),
  channels: z.array(z.enum(MESSAGE_CHANNELS_V3)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export type TicketChannelV3Condition = z.infer<
  typeof ticketChannelV3ConditionSchema
>;

// V3-specific: TICKET_STATUS uses ConversationStatusV3 strings.
// The full schema (including the "has_been" variant) is assembled after
// ResponseTimeOperator is declared below; re-exported from the bottom of this
// file as ticketStatusV3ConditionSchema / TicketStatusV3Condition.
const ticketStatusSelectionConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_STATUS),
  statuses: z.array(z.enum(CONVERSATION_STATUSES_V3)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});

// V3-specific: AI_STATUS — checks the AI engagement status recorded on the
// conversation's thread product-data rows (both `shopper_ai` and `support`).
export const aiStatusConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.AI_STATUS),
  statuses: z.array(z.enum(AI_STATUSES)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export type AiStatusCondition = z.infer<typeof aiStatusConditionSchema>;

export const assignmentV3ConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.ASSIGNMENT),
  mode: z.discriminatedUnion("_tag", [
    z.object({
      _tag: z.literal("type"),
      assignmentTypes: z.array(createEnum(AssignmentTypeOption)),
    }),
    z.object({
      _tag: z.literal("specific_users"),
      mongoUserIds: z.array(z.string()),
    }),
    z.object({
      _tag: z.literal("user_group"),
      userGroupIds: z.array(z.string()),
    }),
  ]),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export type AssignmentV3Condition = z.infer<typeof assignmentV3ConditionSchema>;
export type AssignmentV3ConditionMode = AssignmentV3Condition["mode"];

/**
 * Last Order Condition (V3)
 *
 * Replaces V2's LAST_ORDER_TIME with a more versatile condition that supports
 * multiple order properties: time, value, and item count.
 */
export enum LastOrderProperty {
  TIME = "time",
  VALUE = "value",
  ITEM_COUNT = "item_count",
}

const lastOrderTimePropertySchema = z.object({
  conditionType: z.literal(RuleConditionType.LAST_ORDER),
  property: z.literal(LastOrderProperty.TIME),
  queryOperator: z
    .enum(dateQueryOperators)
    .default(DateQueryOperator.WITHIN_LAST),
  value: z.number().optional(),
  unit: z.nativeEnum(TimeUnit).default(TimeUnit.DAYS),
  date: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

function numericPropertySchemas<T extends LastOrderProperty>(property: T) {
  const base = {
    conditionType: z.literal(RuleConditionType.LAST_ORDER),
    property: z.literal(property),
  };
  return [
    z.object({
      ...base,
      queryOperator: z.literal(NumericQueryOperator.GREATER_THAN),
      value: z.number(),
    }),
    z.object({
      ...base,
      queryOperator: z.literal(NumericQueryOperator.LESS_THAN),
      value: z.number(),
    }),
    z.object({
      ...base,
      queryOperator: z.literal(NumericQueryOperator.EQUAL_TO),
      value: z.number(),
    }),
    z.object({
      ...base,
      queryOperator: z.literal(NumericQueryOperator.BETWEEN),
      minValue: z.number(),
      maxValue: z.number(),
    }),
  ] as const;
}

const lastOrderValuePropertySchemas = numericPropertySchemas(
  LastOrderProperty.VALUE,
);
const lastOrderItemCountPropertySchemas = numericPropertySchemas(
  LastOrderProperty.ITEM_COUNT,
);

export const lastOrderConditionSchema = z.union([
  lastOrderTimePropertySchema,
  ...lastOrderValuePropertySchemas,
  ...lastOrderItemCountPropertySchemas,
]);
export type LastOrderCondition = z.infer<typeof lastOrderConditionSchema>;

/**
 * Time-Based Condition (V3)
 *
 * Replaces V2's AFTER_HOURS with a more versatile condition supporting:
 * - Business hours: checks against team's configured business hours
 * - Weekly schedule: recurring per-day time blocks (e.g. MWF 2-5pm)
 * - Specific date: one-time before/after/between specific dates
 */
export enum TimeBasedMode {
  BUSINESS_HOURS = "business_hours",
  WEEKLY_SCHEDULE = "weekly_schedule",
  SPECIFIC_DATE = "specific_date",
}

export enum SpecificDateOperator {
  BEFORE = "before",
  AFTER = "after",
  BETWEEN = "between",
}

const timeBlockSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
});

const dayScheduleSchema = z.object({
  enabled: z.boolean().default(false),
  timeBlocks: z.array(timeBlockSchema).default([]),
});

const weeklyScheduleSchema = z.object({
  sunday: dayScheduleSchema.optional(),
  monday: dayScheduleSchema.optional(),
  tuesday: dayScheduleSchema.optional(),
  wednesday: dayScheduleSchema.optional(),
  thursday: dayScheduleSchema.optional(),
  friday: dayScheduleSchema.optional(),
  saturday: dayScheduleSchema.optional(),
});

const timeBasedBusinessHoursSchema = z.object({
  conditionType: z.literal(RuleConditionType.TIME_BASED),
  mode: z.literal(TimeBasedMode.BUSINESS_HOURS),
});

const timeBasedWeeklyScheduleSchema = z.object({
  conditionType: z.literal(RuleConditionType.TIME_BASED),
  mode: z.literal(TimeBasedMode.WEEKLY_SCHEDULE),
  timezone: z.string().optional(),
  schedule: weeklyScheduleSchema.optional(),
});

const timeBasedSpecificDateSchema = z.object({
  conditionType: z.literal(RuleConditionType.TIME_BASED),
  mode: z.literal(TimeBasedMode.SPECIFIC_DATE),
  dateOperator: z
    .nativeEnum(SpecificDateOperator)
    .default(SpecificDateOperator.AFTER),
  timezone: z.string().optional(),
  dateTime: z.string().optional(),
  startDateTime: z.string().optional(),
  endDateTime: z.string().optional(),
});

export const timeBasedConditionSchema = z.union([
  timeBasedBusinessHoursSchema,
  timeBasedWeeklyScheduleSchema,
  timeBasedSpecificDateSchema,
]);
export type TimeBasedCondition = z.infer<typeof timeBasedConditionSchema>;

/**
 * Response Time Condition (V3)
 *
 * Evaluates time elapsed since the last response from merchant, customer, or either.
 */
export enum ResponseType {
  MERCHANT = "merchant",
  CUSTOMER = "customer",
  EITHER = "either",
}

export enum ResponseTimeOperator {
  MORE_THAN = "more_than",
  LESS_THAN = "less_than",
  BETWEEN = "between",
}

const responseTimeBaseFields = {
  conditionType: z.literal(RuleConditionType.RESPONSE_TIME),
  responseType: z.nativeEnum(ResponseType).default(ResponseType.EITHER),
  unit: z.nativeEnum(TimeUnit).default(TimeUnit.HOURS),
};

const responseTimeMoreThanSchema = z.object({
  ...responseTimeBaseFields,
  responseTimeOperator: z.literal(ResponseTimeOperator.MORE_THAN),
  value: z.number(),
});

const responseTimeLessThanSchema = z.object({
  ...responseTimeBaseFields,
  responseTimeOperator: z.literal(ResponseTimeOperator.LESS_THAN),
  value: z.number(),
});

const responseTimeBetweenSchema = z.object({
  ...responseTimeBaseFields,
  responseTimeOperator: z.literal(ResponseTimeOperator.BETWEEN),
  minValue: z.number(),
  maxValue: z.number(),
});

export const responseTimeConditionSchema = z.discriminatedUnion(
  "responseTimeOperator",
  [
    responseTimeMoreThanSchema,
    responseTimeLessThanSchema,
    responseTimeBetweenSchema,
  ],
);
export type ResponseTimeCondition = z.infer<typeof responseTimeConditionSchema>;

/**
 * Ticket Status Condition — "has been" variant (V3)
 *
 * Checks whether the ticket has been in one of the specified statuses for
 * more than / less than / between X time units (using statusUpdatedAt).
 * Assembled here so it can reuse ResponseTimeOperator defined above.
 */
const ticketStatusHasBeenBaseFields = {
  conditionType: z.literal(RuleConditionType.TICKET_STATUS),
  statuses: z.array(z.enum(CONVERSATION_STATUSES_V3)),
  queryOperator: z.literal(QueryOperator.HAS_BEEN),
  unit: z.nativeEnum(TimeUnit).default(TimeUnit.HOURS),
};

const ticketStatusHasBeenMoreThanSchema = z.object({
  ...ticketStatusHasBeenBaseFields,
  statusTimeOperator: z.literal(ResponseTimeOperator.MORE_THAN),
  value: z.number(),
});

const ticketStatusHasBeenLessThanSchema = z.object({
  ...ticketStatusHasBeenBaseFields,
  statusTimeOperator: z.literal(ResponseTimeOperator.LESS_THAN),
  value: z.number(),
});

const ticketStatusHasBeenBetweenSchema = z.object({
  ...ticketStatusHasBeenBaseFields,
  statusTimeOperator: z.literal(ResponseTimeOperator.BETWEEN),
  minValue: z.number(),
  maxValue: z.number(),
});

export const ticketStatusHasBeenConditionSchema = z.discriminatedUnion(
  "statusTimeOperator",
  [
    ticketStatusHasBeenMoreThanSchema,
    ticketStatusHasBeenLessThanSchema,
    ticketStatusHasBeenBetweenSchema,
  ],
);
export type TicketStatusHasBeenCondition = z.infer<
  typeof ticketStatusHasBeenConditionSchema
>;

export const ticketStatusV3ConditionSchema = z.union([
  ticketStatusSelectionConditionSchema,
  ticketStatusHasBeenConditionSchema,
]);
export type TicketStatusV3Condition = z.infer<
  typeof ticketStatusV3ConditionSchema
>;

/**
 * Message Count Condition (V3)
 *
 * Evaluates the number of messages on a support ticket against a numeric
 * threshold. Counts every non-deleted message linked to the ticket's topic
 * (both customer- and merchant-authored).
 */
const messageCountBase = {
  conditionType: z.literal(RuleConditionType.MESSAGE_COUNT),
};

const messageCountGreaterThanSchema = z.object({
  ...messageCountBase,
  queryOperator: z.literal(NumericQueryOperator.GREATER_THAN),
  value: z.number(),
});

const messageCountLessThanSchema = z.object({
  ...messageCountBase,
  queryOperator: z.literal(NumericQueryOperator.LESS_THAN),
  value: z.number(),
});

const messageCountEqualToSchema = z.object({
  ...messageCountBase,
  queryOperator: z.literal(NumericQueryOperator.EQUAL_TO),
  value: z.number(),
});

const messageCountBetweenSchema = z.object({
  ...messageCountBase,
  queryOperator: z.literal(NumericQueryOperator.BETWEEN),
  minValue: z.number(),
  maxValue: z.number(),
});

export const messageCountConditionSchema = z.discriminatedUnion(
  "queryOperator",
  [
    messageCountGreaterThanSchema,
    messageCountLessThanSchema,
    messageCountEqualToSchema,
    messageCountBetweenSchema,
  ],
);
export type MessageCountCondition = z.infer<typeof messageCountConditionSchema>;

/**
 * Sent From / Received At Conditions (V3)
 *
 * Replaces V2's SENT_FROM_EMAIL and RECEIVED_AT_EMAIL with more generic
 * conditions. Currently supports email channel, designed to be extensible
 * for additional channels in the future.
 */
export enum ContactChannel {
  EMAIL = "email",
}

export const sentFromConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.SENT_FROM),
  channel: z.nativeEnum(ContactChannel).default(ContactChannel.EMAIL),
  values: z.array(z.string()).optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.CONTAINS),
});
export type SentFromCondition = z.infer<typeof sentFromConditionSchema>;

export const receivedAtConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.RECEIVED_AT),
  channel: z.nativeEnum(ContactChannel).default(ContactChannel.EMAIL),
  values: z.array(z.string()).optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.IS_EXACTLY),
});
export type ReceivedAtCondition = z.infer<typeof receivedAtConditionSchema>;

export const ruleV3ConditionSchema = z.union([
  ticketChannelV3ConditionSchema,
  ticketStatusV3ConditionSchema,
  topicConditionSchema,
  sentFromConditionSchema,
  receivedAtConditionSchema,
  subjectLineConditionSchema,
  messageBodyMatchesConditionSchema,
  timeBasedConditionSchema,
  assignmentV3ConditionSchema,
  lastOrderConditionSchema,
  messageSentimentConditionSchema,
  hasTagConditionSchema,
  responseTimeConditionSchema,
  aiStatusConditionSchema,
  messageCountConditionSchema,
]);
export type RuleV3Condition = z.infer<typeof ruleV3ConditionSchema>;

const conditionGroupV3Operator = z.enum(["AND", "OR"]);
export type ConditionGroupV3Operator = z.infer<typeof conditionGroupV3Operator>;

const conditionGroupV3Schema_: z.ZodType<ConditionGroupV3> = z.lazy(() =>
  z.object({
    operator: conditionGroupV3Operator,
    conditions: z.array(
      z.union([ruleV3ConditionSchema, conditionGroupV3Schema_]),
    ),
  }),
);
export const conditionGroupV3Schema: z.ZodType<ConditionGroupV3> =
  conditionGroupV3Schema_;

export interface ConditionGroupV3 {
  operator: ConditionGroupV3Operator;
  conditions: (RuleV3Condition | ConditionGroupV3)[];
}

// Base step schema
const baseStepSchema = z.object({
  customTitle: z.string().optional(),
  type: z.string(),
  id: z.string(),
});

const ruleV3ConditionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_CONDITIONS_V3),
  conditionGroups: conditionGroupV3Schema,
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
});
export const ruleV3ConditionsStepSchema: z.ZodType<RuleV3ConditionsStep> =
  ruleV3ConditionsStepSchema_;
export interface RuleV3ConditionsStep
  extends z.infer<typeof ruleV3ConditionsStepSchema_> {}

const ruleV3ActionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_ACTIONS_V3),
  actions: z.array(ruleActionSchema),
  errorHandlingStrategy: createEnum(ErrorHandlingStrategy).default(
    ErrorHandlingStrategy.FAIL_FAST,
  ),
  nextId: z.string().optional(),
});
export const ruleV3ActionsStepSchema: z.ZodType<RuleV3ActionsStep> =
  ruleV3ActionsStepSchema_;
export interface RuleV3ActionsStep
  extends z.infer<typeof ruleV3ActionsStepSchema_> {}
